import React, { useState, useMemo } from 'react';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { useTranslation } from 'react-i18next';

import { useRequestAllMeterValuesQuery } from '../../redux/api/meterValues';
import Chart from '../Utils/Chart';
import IfRender from '../Utils/IfRender';
import LoadingAnimation from '../Utils/BateryChargeAnimation';
import NoData from '../Utils/NoData';
import FormSelect from '../Utils/Form/FormSelect';
import MeterValuesTable from './meterValuesTable';

import { DATE_FORMAT, GRAPH_TYPES } from '../../Constants';
import { chargerMeters } from '../../QueryFilters/queries';

const MeterReportData = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const sessionId = urlParams.get('id');
  const { t } = useTranslation();

  const [chartType, setChartType] = useState(t('all'));

  const { data: { list: meterValuesList = [] } = {}, isLoading } = useRequestAllMeterValuesQuery({
    sessionId,
    filter: JSON.stringify(chargerMeters)
  });

  const chartDataSource = useMemo(() => {
    const voltage = { l1: [], l2: [], l3: [] };
    const current = { l1: [], l2: [], l3: [] };
    const temp = [];
    const power = [];
    const energy = [];
    const firstEnergy = meterValuesList[0]?.energyActiveImportRegisterValue || 0;
    meterValuesList.forEach((data) => {
      const time = new Date(data.timestamp).getTime();
      voltage.l1.push({ x: time, y: parseFloat(data.l1VoltageValue) });
      voltage.l2.push({ x: time, y: parseFloat(data.l2VoltageValue) });
      voltage.l3.push({ x: time, y: parseFloat(data.l3VoltageValue) });
      current.l1.push({ x: time, y: parseFloat(data.l1CurrentImportValue) });
      current.l2.push({ x: time, y: parseFloat(data.l2CurrentImportValue) });
      current.l3.push({ x: time, y: parseFloat(data.l3CurrentImportValue) });
      temp.push({ x: time, y: parseFloat(data.temperature) });
      power.push({ x: time, y: parseFloat(data.powerActiveImportValue) });
      energy.push({ x: time, y: parseFloat((data.energyActiveImportRegisterValue - firstEnergy).toFixed(0)) });
    });

    return [
      {
        title: t('voltage'),
        value: 'Volt (V)',
        chartData: [
          { data: voltage.l1, name: `L1` },
          { data: voltage.l2, name: `L2` },
          { data: voltage.l3, name: `L3` }
        ]
      },
      {
        title: t('current'),
        value: 'Ampere (A)',
        chartData: [
          { data: current.l1, name: `L1` },
          { data: current.l2, name: `L2` },
          { data: current.l3, name: `L3` }
        ]
      },
      {
        title: t('temperature'),
        value: 'Celsius (°C)',
        chartData: [{ data: temp, name: t('temperature') }]
      },
      {
        title: t('power'),
        value: 'Watt (W)',
        chartData: [{ data: power, name: t('power') }]
      },
      {
        title: t('energy'),
        value: 'Watt/Hour (Wh)',
        chartData: [{ data: energy, name: t('energy') }]
      }
    ];
  }, [meterValuesList, t]);

  const handleChange = (event) => {
    setChartType(event.target.value);
  };

  const graphTypes = GRAPH_TYPES.map((type) => t(`${type.toLowerCase()}`));

  return (
    <div className='page-content'>
      <IfRender condition={!isLoading} otherwise={<LoadingAnimation />}>
        <IfRender condition={meterValuesList.length > 0} otherwise={<NoData />}>
          <Tabs>
            <TabList>
              <Tab>{t('chart')}</Tab>
              <Tab>{t('details')}</Tab>
            </TabList>

            <TabPanel>
              <FormSelect
                id='graph-types'
                value={chartType}
                options={graphTypes}
                onChange={(e) => handleChange(e)}
              />
              <Chart chartName={chartType} data={chartDataSource} format={DATE_FORMAT.hour} />
            </TabPanel>

            <TabPanel>
              <MeterValuesTable tableData={meterValuesList} />
            </TabPanel>
          </Tabs>
        </IfRender>
      </IfRender>
    </div>
  );
};

export default MeterReportData;
